import { URLS, resolvePublicServiceUrl } from "@itaas/ports";
import type { ProductCategory } from "./product-catalog";
import type {
  PepCatalogLoadResult,
  PepProductCard,
} from "./pep-catalog.types";

const CACHE_TTL_MS = 5 * 60 * 1000;

const PEP_CATEGORY_MAP: Record<string, ProductCategory> = {
  hr: "people",
  vertical: "sector",
  operations: "operations",
  finance: "commerce",
  erp: "operations",
  platform: "platform",
  infrastructure: "platform",
  integration: "platform",
};

const MODULE_ICON_HINTS: Record<string, string> = {
  rh: "users",
  edu: "school",
  hospitol: "building-hospital",
  inventory: "building-warehouse",
  erp: "building-factory-2",
  mineportal: "mountain",
  subportal: "users-group",
  crm: "heart-handshake",
  billing: "receipt",
  finance: "chart-line",
  comex: "ship",
  legal: "scale",
  security: "shield-check",
  product: "package",
  autoid: "scan",
  notifications: "bell",
};

const MODULE_ACCENT_HINTS: Record<string, string> = {
  rh: "#0d6efd",
  edu: "#8b5cf6",
  hospitol: "#06b6d4",
  inventory: "#f97316",
  mineportal: "#ca8a04",
  subportal: "#a16207",
  comex: "#0369a1",
};

export interface PepMarketingOverlay {
  tagline?: string;
  benefits?: string[];
  icon?: string;
  accent?: string;
  category?: ProductCategory;
  trialAvailable?: boolean;
  featured?: boolean;
  displayName?: string;
  description?: string;
}

let pepOverlayByModuleId: Record<string, PepMarketingOverlay> = {};
let cache: PepCatalogLoadResult | null = null;

const DEFAULT_PEP_API = "http://localhost:3100/api/v1";
const DEFAULT_PEP_WEB = "http://localhost:3101";

function stripTrailingSlash(url: string): string {
  return url.replace(/\/$/, "");
}

function normalizeServiceUrl(
  value: string | undefined | null,
  fallback: string,
): string {
  const trimmed = value?.trim();
  return stripTrailingSlash(trimmed || fallback);
}

export function resolvePepApiBase(override?: string): string {
  if (override?.trim()) return stripTrailingSlash(override.trim());
  const envBase =
    typeof import.meta !== "undefined"
      ? (import.meta as ImportMeta & { env?: Record<string, string> }).env
          ?.VITE_PEP_API_BASE
      : undefined;
  if (envBase?.trim()) return stripTrailingSlash(envBase.trim());
  return normalizeServiceUrl(URLS?.PEP_API, DEFAULT_PEP_API);
}

/** URL publique PEP (catalogue web) — env requis en production. */
export function resolvePepWebBase(override?: string): string {
  if (override?.trim()) return stripTrailingSlash(override.trim());
  const envBase =
    typeof import.meta !== "undefined"
      ? (import.meta as ImportMeta & { env?: Record<string, string> }).env
          ?.VITE_PEP_URL
      : undefined;
  return resolvePublicServiceUrl(
    envBase,
    "VITE_PEP_URL",
    URLS?.PEP_WEB ?? DEFAULT_PEP_WEB,
  );
}

function mapPepProductToOverlay(card: PepProductCard): PepMarketingOverlay {
  const moduleId = card.moduleId ?? card.slug;
  const tagline =
    card.taglineFr?.trim() ||
    card.tagline?.trim() ||
    card.descriptionFr?.trim() ||
    card.description?.trim();

  return {
    displayName: card.displayNameFr?.trim() || card.displayName,
    description: card.descriptionFr?.trim() || card.description,
    tagline,
    benefits:
      card.valuePropositions?.slice(0, 3) ??
      card.features?.slice(0, 3) ??
      [],
    icon: MODULE_ICON_HINTS[moduleId] ?? "box",
    accent: MODULE_ACCENT_HINTS[moduleId] ?? "#64748b",
    category: PEP_CATEGORY_MAP[card.category] ?? "platform",
    trialAvailable: card.status !== "reserved" && card.status !== "planned",
    featured: card.status === "ga",
  };
}

export function applyPepCatalogOverlay(products: PepProductCard[]): void {
  const next: Record<string, PepMarketingOverlay> = {};
  for (const card of products) {
    const moduleId = card.moduleId ?? card.slug;
    next[moduleId] = mapPepProductToOverlay(card);
  }
  pepOverlayByModuleId = next;
}

export function getPepMarketingOverlay(
  moduleId: string
): PepMarketingOverlay | undefined {
  return pepOverlayByModuleId[moduleId];
}

export function clearPepCatalogCache(): void {
  cache = null;
  pepOverlayByModuleId = {};
}

export async function loadPepProductCatalog(
  apiBaseOverride?: string
): Promise<PepCatalogLoadResult> {
  const now = Date.now();
  if (cache && now - cache.loadedAt < CACHE_TTL_MS) {
    return cache;
  }

  const apiBase = resolvePepApiBase(apiBaseOverride);

  try {
    const res = await fetch(`${apiBase}/catalog/products`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new Error(`PEP catalog HTTP ${res.status}`);
    }
    const products = (await res.json()) as PepProductCard[];
    applyPepCatalogOverlay(products);
    cache = { source: "pep", products, loadedAt: now };
    return cache;
  } catch {
    cache = { source: "static", products: [], loadedAt: now };
    return cache;
  }
}
